import { RefreshIcon } from "@/assets/icons";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import InfoNote from "@/components/ui/InfoNote";
import { DATA_SETTINGS_MESSAGE } from "@/constants/message";
import {
  DataSettingPopupType,
  DataSettings,
  EDataSettingPopupType,
  EDataSettingSpatialType
} from "@/types/dataSetting";

import { Button } from "equator-design-system";

import { HUB_MULTIPLE_GEOMETRY_OPTIONS } from "../dataSettingOptions";
import { isSaveDataSettingDisabled } from "../dataSettingValidation";
import MultipleGeometrySetting from "../MultipleGeometrySetting";
import NoGeometrySetting from "../NoGeometrySetting";
import SingleGeometrySetting from "../SingleGeometrySetting";
import ViewHeader from "./ViewHeader";

const TEST_ID_PREFIX = "dataset-management-spatial-settings-view";

type SpatialSettingsViewProps = {
  type: DataSettingPopupType;
  dataSetting: DataSettings | null;
  attributesLatitude: IOptionItem[];
  attributesLongitude: IOptionItem[];
  attributesForAdminArea: IOptionItem[];
  isCoordinateRejected?: boolean;
  isSaving: boolean;
  setDataSetting: React.Dispatch<React.SetStateAction<DataSettings | null>>;
  onBack: () => void;
  onCancel: () => void;
  onClose: () => void;
  onSave: () => void;
};

const SpatialSettingsView = ({
  type,
  dataSetting,
  attributesLatitude,
  attributesLongitude,
  attributesForAdminArea,
  isCoordinateRejected = false,
  isSaving,
  setDataSetting,
  onBack,
  onCancel,
  onClose,
  onSave
}: SpatialSettingsViewProps) => {
  const handleSpatialAggregationSelect = <
    K extends keyof NonNullable<DataSettings["spatialAggregation"]>
  >(
    key: K,
    value: NonNullable<DataSettings["spatialAggregation"]>[K]
  ) => {
    setDataSetting((prevDataSetting: DataSettings | null) => {
      if (prevDataSetting === null) {
        return { spatialAggregation: { [key]: value } };
      }
      return {
        ...prevDataSetting,
        spatialAggregation: {
          ...prevDataSetting.spatialAggregation,
          [key]: value
        }
      };
    });
  };

  // Warns about the code column format once ADM Area is picked as the
  // aggregation (never for a single geometry, which is auto-selected). Hidden
  // when there is no column to pick, since the empty message already covers that.
  const isAdmAreaCodeNoteShown =
    type !== EDataSettingPopupType.Single &&
    attributesForAdminArea.length > 0 &&
    dataSetting?.spatialAggregation?.type?.value ===
      EDataSettingSpatialType.Admin_Area;

  return (
    <div className="flex flex-col">
      <ViewHeader
        title={DATA_SETTINGS_MESSAGE.SPATIAL_TITLE}
        isDisabled={isSaving}
        onBack={onBack}
        onClose={onClose}
      />

      {type === EDataSettingPopupType.None && (
        <NoGeometrySetting
          attributesForAdminArea={attributesForAdminArea}
          attributesLatitude={attributesLatitude}
          attributesLongitude={attributesLongitude}
          dataSetting={dataSetting}
          handleSpatialAggregationSelect={handleSpatialAggregationSelect}
          emptyAttributesMessage={
            DATA_SETTINGS_MESSAGE.SPATIAL_NO_ATTRIBUTES_NOTE
          }
        />
      )}
      {type === EDataSettingPopupType.Single && (
        <SingleGeometrySetting
          dataSetting={dataSetting}
          handleSpatialAggregationSelect={handleSpatialAggregationSelect}
        />
      )}
      {type === EDataSettingPopupType.Multiple && (
        <MultipleGeometrySetting
          dataSetting={dataSetting}
          emptyAttributesMessage={
            DATA_SETTINGS_MESSAGE.SPATIAL_NO_ATTRIBUTES_NOTE
          }
          aggregationOptions={HUB_MULTIPLE_GEOMETRY_OPTIONS}
          attributesForAdminArea={attributesForAdminArea}
          handleSpatialAggregationSelect={handleSpatialAggregationSelect}
        />
      )}

      {isCoordinateRejected && (
        <p
          role="alert"
          className="mt-4 text-xs text-red-500"
          data-testid={`${TEST_ID_PREFIX}-coordinate-error`}
        >
          {DATA_SETTINGS_MESSAGE.SPATIAL_COORDINATE_REJECTED}
        </p>
      )}
      {isAdmAreaCodeNoteShown && (
        <InfoNote
          variant="muted"
          className="mt-4"
          message={DATA_SETTINGS_MESSAGE.SPATIAL_ADM_AREA_CODE_NOTE}
          data-testid={`${TEST_ID_PREFIX}-adm-area-note`}
        />
      )}

      <div className="flex gap-2 w-full pt-4">
        <Button
          className="h-[40px] w-full btn-secondary text-sm !font-bold disabled:bg-white"
          disabled={isSaving}
          onClick={onCancel}
          data-testid={`${TEST_ID_PREFIX}-cancel-button`}
        >
          <span>Cancel</span>
        </Button>
        <Button
          className="h-[40px] w-full btn-primary text-sm !font-bold"
          type="submit"
          disabled={isSaving || isSaveDataSettingDisabled(type, dataSetting)}
          onClick={onSave}
          data-testid={`${TEST_ID_PREFIX}-save-button`}
        >
          {isSaving && (
            <RefreshIcon className="w-4 h-4 animate-spin mr-2 fill-white" />
          )}
          <span>{isSaving ? "Validating…" : "Save"}</span>
        </Button>
      </div>
    </div>
  );
};

export default SpatialSettingsView;
